import React, { useState } from "react";
import { Props } from "./interfaces";
import { Card, Grid, Typography, useTheme, lighten, Checkbox, Dialog, Divider, Tooltip } from "@mui/material";
import {
  BlockDocumentAndDate,
  SectionSubject,
  SectionFrom,
  SectionTo,
  BlockStatuses,
  BlockEdit,
  DateContainer,
  StatusContainer,
  BlockSelect,
  CardWrapper,
  StyledCard,
  UndefinedFieldText,
  BlockSubjectFromAndTo,
  SectionFromAndTo,
  DocumentViewerContainer,
  RecipientsContainer,
  AddresseeNicknameContainer,
  AddresseNickname,
} from "./styles";
import { intlFormatDateShort, listOfMonths } from "../../../../utils/time-utils";
import { useTranslation } from "react-i18next";
import { FileIconButton } from "../../../AttachedFiles/styles";
import { DefaultExtensionType, FileIcon } from "react-file-icon";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight, faCircleCheck, faCircleExclamation, faEllipsisV } from "@fortawesome/free-solid-svg-icons";
import { INCOMING_MAIL_STATUSES, MAIL_CLASSIFICATION_STATUS } from "../../../../utils/constants/constants";
import SimplePopover from "../../../../reusable-components/SimplePopover/SimplePopover";
import { getItemPeriodValue } from "../../../../models/documents/period";
import { getFileExtension } from "src/utils/files-utils/get-file-extension";
import { customFileIconStyles } from "src/reusable-components/DefaultFileIconStyles/default-file-icon-styles";
import IncomingMailActions from "../IncomingMailActions/IncomingMailActions";
import DocumentViewer from "src/reusable-components/DocumentViewer/DocumentViewer";
import DialogTitle from "src/containers/DialogTitle/DialogTitle";
import { useAppSelector } from "src/custom-hooks/redux/redux";
import SuggestedByAiHint from "src/views/manage-mail/SuggestedByAiHint";

const IncomingMailCard = ({
  incomingMail,
  handleNotifyParent,
  canUpload,
  canClassify,
  canRead,
  isOnlyRecipient,
}: Props) => {
  const tRP = "components.incoming_mail.incoming_mail_card";
  const { t } = useTranslation();
  const theme = useTheme();

  const { currentUserAuthorizations } = useAppSelector((state) => state.currentUserReducer);
  const isAdmin = currentUserAuthorizations?.canChangeAll;

  const [viewFile, setViewFile] = useState<boolean>(false);

  return (
    <>
      <CardWrapper>
        <StyledCard>
          {(isAdmin || canUpload) && (
            <BlockSelect>
              <Checkbox />
            </BlockSelect>
          )}

          <Divider
            orientation="vertical"
            style={{
              borderColor: lighten(theme.palette.secondary.light, 0.6),
              alignSelf: "center",
              height: "85%",
            }}
          />

          <BlockDocumentAndDate flex={1}>
            {incomingMail.document?.files[0] && (
              <FileIconButton onClick={() => setViewFile(true)}>
                <FileIcon
                  extension={getFileExtension(incomingMail.document.files[0].name)}
                  {...customFileIconStyles[
                    getFileExtension(incomingMail.document.files[0].name) as DefaultExtensionType
                  ]}
                />
              </FileIconButton>
            )}
            <Typography variant="body2">{incomingMail.createdByUser?.name}</Typography>
            <DateContainer>
              <Typography variant="h5">{new Date(incomingMail.createdAt).getDate()}</Typography>
              <Grid>
                <Typography variant="body2">
                  {t(`months.${listOfMonths[new Date(incomingMail.createdAt).getMonth()]}`)}
                </Typography>
                <Typography variant="body2">{new Date(incomingMail.createdAt).getFullYear()}</Typography>
              </Grid>
            </DateContainer>
          </BlockDocumentAndDate>

          <Divider
            orientation="vertical"
            style={{
              borderColor: lighten(theme.palette.secondary.light, 0.6),
              alignSelf: "center",
              height: "85%",
            }}
          />

          <BlockSubjectFromAndTo flex={5}>
            <SectionSubject>
              {incomingMail.subject ? (
                <Typography variant="h5">{incomingMail.subject?.subject}</Typography>
              ) : incomingMail.subjectOcr ? (
                <>
                  <Typography variant="h5">{incomingMail.subjectOcr.subject}</Typography>
                  <SuggestedByAiHint />
                </>
              ) : (
                <UndefinedFieldText variant="h5">{`${t(`${tRP}.no_subject`)}`}</UndefinedFieldText>
              )}
            </SectionSubject>

            <SectionFromAndTo>
              <SectionFrom>
                {incomingMail.receivedFrom ? (
                  <Typography>{incomingMail.receivedFrom?.name}</Typography>
                ) : incomingMail.receivedFromOcr ? (
                  <>
                    <Typography>{incomingMail.receivedFromOcr?.name}</Typography>
                    <SuggestedByAiHint />
                  </>
                ) : (
                  <UndefinedFieldText>{`${t(`${tRP}.no_sender`)}`}</UndefinedFieldText>
                )}
              </SectionFrom>

              <FontAwesomeIcon
                icon={faArrowRight}
                style={{ alignSelf: "center" }}
              />
              <SectionTo>
                {incomingMail.addressedTo ? (
                  <>
                    <AddresseeNicknameContainer>
                      <AddresseNickname variant="h5">{incomingMail.addressedTo.nickname}</AddresseNickname>
                    </AddresseeNicknameContainer>

                    {!isOnlyRecipient && (
                      <RecipientsContainer>
                        <Typography
                          variant="body2"
                          color={theme.palette.secondary.main}
                        >
                          {`${t(`${tRP}.email_to`)}`} :{" "}
                        </Typography>
                        <Grid>
                          {incomingMail.addressedTo.recipients.slice(0, 3).map((recipient) => (
                            <Typography variant="body2">
                              {recipient.user?.name} &nbsp;
                              {!!incomingMail.sendingNotifications.find(
                                (sendingNotification) =>
                                  sendingNotification.recipient?.id === recipient.id &&
                                  sendingNotification.notification?.isDelivered
                              ) && (
                                <Tooltip title={`${t(`${tRP}.successful_email`)}`}>
                                  <span>
                                    <FontAwesomeIcon
                                      icon={faCircleCheck}
                                      color={theme.palette.success.main}
                                    />
                                  </span>
                                </Tooltip>
                              )}
                              {!!incomingMail.sendingNotifications.find(
                                (sendingNotification) =>
                                  sendingNotification.recipient?.id === recipient.id &&
                                  !sendingNotification.notification?.isDelivered
                              ) && (
                                <Tooltip
                                  title={`${t(`${tRP}.not_delivered`)} : ${
                                    incomingMail.sendingNotifications.find(
                                      (sendingNotification) =>
                                        sendingNotification.recipient?.id === recipient.id &&
                                        !sendingNotification.notification?.isDelivered
                                    )?.notification?.error ?? ""
                                  }`}
                                >
                                  <span>
                                    <FontAwesomeIcon
                                      icon={faCircleExclamation}
                                      color={theme.palette.error.main}
                                    />
                                  </span>
                                </Tooltip>
                              )}
                            </Typography>
                          ))}
                        </Grid>
                        {incomingMail.addressedTo.recipients.length > 3 && (
                          <SimplePopover
                            buttonType="icon"
                            customButtonStyle={{
                              alignSelf: "center",
                            }}
                            buttonContent={
                              <Grid
                                style={{
                                  backgroundColor: theme.palette.background.paper,
                                  color: theme.palette.primary.main,
                                  borderRadius: "50px",
                                  padding: "4px",
                                  width: "36px",
                                  fontSize: "12px",
                                }}
                              >
                                {" "}
                                + {incomingMail.addressedTo.recipients.length - 3}
                              </Grid>
                            }
                          >
                            <Grid
                              style={{
                                padding: "15px",
                                display: "flex",
                                flexDirection: "column",
                                gap: "5px",
                              }}
                            >
                              {incomingMail.addressedTo.recipients.slice(3).map((recipient) => (
                                <Typography variant="body2">
                                  {recipient.user?.name}
                                  {!!incomingMail.sendingNotifications.find(
                                    (sendingNotification) =>
                                      sendingNotification.recipient?.id === recipient.id &&
                                      sendingNotification.notification?.isDelivered
                                  ) && (
                                    <Tooltip title={`${t(`${tRP}.successful_email`)}`}>
                                      <span>
                                        <FontAwesomeIcon
                                          icon={faCircleCheck}
                                          color={theme.palette.success.main}
                                        />
                                      </span>
                                    </Tooltip>
                                  )}
                                  {!!incomingMail.sendingNotifications.find(
                                    (sendingNotification) =>
                                      sendingNotification.recipient?.id === recipient.id &&
                                      !sendingNotification.notification?.isDelivered
                                  ) && (
                                    <Tooltip
                                      title={`${t(`${tRP}.not_delivered`)} : ${
                                        incomingMail.sendingNotifications.find(
                                          (sendingNotification) =>
                                            sendingNotification.recipient?.id === recipient.id &&
                                            !sendingNotification.notification?.isDelivered
                                        )?.notification?.error ?? ""
                                      }`}
                                    >
                                      <span>
                                        <FontAwesomeIcon
                                          icon={faCircleExclamation}
                                          color={theme.palette.error.main}
                                        />
                                      </span>
                                    </Tooltip>
                                  )}
                                </Typography>
                              ))}
                            </Grid>
                          </SimplePopover>
                        )}
                      </RecipientsContainer>
                    )}
                  </>
                ) : incomingMail.adressedToOcr && !isOnlyRecipient ? (
                  <>
                    <AddresseeNicknameContainer>
                      <AddresseNickname variant="h5">{incomingMail.adressedToOcr?.nickname}</AddresseNickname>
                      <SuggestedByAiHint />
                    </AddresseeNicknameContainer>
                    <RecipientsContainer>
                      <Typography
                        variant="body2"
                        color={theme.palette.secondary.main}
                      >
                        {`${t(`${tRP}.email_to`)}`} :{" "}
                      </Typography>
                      <Grid>
                        {incomingMail.adressedToOcr.recipients.slice(0, 3).map((recipient, idx) => (
                          <Typography
                            variant="body2"
                            key={idx}
                          >
                            {recipient.user?.name} &nbsp;
                            {!!incomingMail.sendingNotifications.find(
                              (sendingNotification) =>
                                sendingNotification.recipient?.id === recipient.id &&
                                sendingNotification.notification?.isDelivered
                            ) && (
                              <Tooltip title={`${t(`${tRP}.successful_email`)}`}>
                                <span>
                                  <FontAwesomeIcon
                                    icon={faCircleCheck}
                                    color={theme.palette.success.main}
                                  />
                                </span>
                              </Tooltip>
                            )}
                            {!!incomingMail.sendingNotifications.find(
                              (sendingNotification) =>
                                sendingNotification.recipient?.id === recipient.id &&
                                !sendingNotification.notification?.isDelivered
                            ) && (
                              <Tooltip
                                title={`${t(`${tRP}.not_delivered`)} : ${
                                  incomingMail.sendingNotifications.find(
                                    (sendingNotification) =>
                                      sendingNotification.recipient?.id === recipient.id &&
                                      !sendingNotification.notification?.isDelivered
                                  )?.notification?.error ?? ""
                                }`}
                              >
                                <span>
                                  <FontAwesomeIcon
                                    icon={faCircleExclamation}
                                    color={theme.palette.error.main}
                                  />
                                </span>
                              </Tooltip>
                            )}
                          </Typography>
                        ))}
                      </Grid>
                      {incomingMail.adressedToOcr.recipients.length > 3 && (
                        <SimplePopover
                          buttonType="icon"
                          customButtonStyle={{
                            alignSelf: "center",
                          }}
                          buttonContent={
                            <Grid
                              style={{
                                backgroundColor: theme.palette.background.paper,
                                color: theme.palette.primary.main,
                                borderRadius: "50px",
                                padding: "4px",
                                width: "36px",
                                fontSize: "12px",
                              }}
                            >
                              {" "}
                              + {incomingMail.adressedToOcr.recipients.length - 3}
                            </Grid>
                          }
                        >
                          <Grid
                            style={{
                              padding: "15px",
                              display: "flex",
                              flexDirection: "column",
                              gap: "5px",
                            }}
                          >
                            {incomingMail.adressedToOcr.recipients.slice(3).map((recipient, idx) => (
                              <Typography
                                variant="body2"
                                key={idx}
                              >
                                {recipient.user?.name}
                                {!!incomingMail.sendingNotifications.find(
                                  (sendingNotification) =>
                                    sendingNotification.recipient?.id === recipient.id &&
                                    sendingNotification.notification?.isDelivered
                                ) && (
                                  <Tooltip title={`${t(`${tRP}.successful_email`)}`}>
                                    <span>
                                      <FontAwesomeIcon
                                        icon={faCircleCheck}
                                        color={theme.palette.success.main}
                                      />
                                    </span>
                                  </Tooltip>
                                )}
                                {!!incomingMail.sendingNotifications.find(
                                  (sendingNotification) =>
                                    sendingNotification.recipient?.id === recipient.id &&
                                    !sendingNotification.notification?.isDelivered
                                ) && (
                                  <Tooltip
                                    title={`${t(`${tRP}.not_delivered`)} : ${
                                      incomingMail.sendingNotifications.find(
                                        (sendingNotification) =>
                                          sendingNotification.recipient?.id === recipient.id &&
                                          !sendingNotification.notification?.isDelivered
                                      )?.notification?.error ?? ""
                                    }`}
                                  >
                                    <span>
                                      <FontAwesomeIcon
                                        icon={faCircleExclamation}
                                        color={theme.palette.error.main}
                                      />
                                    </span>
                                  </Tooltip>
                                )}
                              </Typography>
                            ))}
                          </Grid>
                        </SimplePopover>
                      )}
                    </RecipientsContainer>
                  </>
                ) : (
                  <UndefinedFieldText>{`${t(`${tRP}.no_addressee`)}`}</UndefinedFieldText>
                )}
              </SectionTo>
            </SectionFromAndTo>
          </BlockSubjectFromAndTo>

          <BlockStatuses flex={1.5}>
            <StatusContainer
              flex={1}
              style={{
                backgroundColor:
                  incomingMail.status?.id === INCOMING_MAIL_STATUSES.Pending.id
                    ? lighten(theme.palette.primary.light, 0.5)
                    : incomingMail.status?.id === INCOMING_MAIL_STATUSES.Emailed.id
                    ? lighten(theme.palette.success.light, 0.5)
                    : lighten(theme.palette.secondary.light, 0.5),
              }}
            >
              <Typography
                style={{
                  color:
                    incomingMail.status?.id === INCOMING_MAIL_STATUSES.Pending.id
                      ? theme.palette.primary.main
                      : incomingMail.status?.id === INCOMING_MAIL_STATUSES.Emailed.id
                      ? theme.palette.success.main
                      : theme.palette.secondary.main,
                }}
              >
                <b>{`${t(`incoming_mail_statuses.${incomingMail.status?.name}`)}`}</b>
              </Typography>
            </StatusContainer>
            <StatusContainer
              flex={1}
              style={{
                backgroundColor:
                  incomingMail.classificationStatus?.id === MAIL_CLASSIFICATION_STATUS.ToBeClassified.id
                    ? lighten(theme.palette.primary.light, 0.5)
                    : incomingMail.classificationStatus?.id === MAIL_CLASSIFICATION_STATUS.Classified.id
                    ? lighten(theme.palette.success.main, 0.8)
                    : lighten(theme.palette.secondary.light, 0.5),
              }}
            >
              <Typography
                style={{
                  color:
                    incomingMail.classificationStatus?.id === MAIL_CLASSIFICATION_STATUS.ToBeClassified.id
                      ? theme.palette.primary.main
                      : incomingMail.classificationStatus?.id === MAIL_CLASSIFICATION_STATUS.Classified.id
                      ? theme.palette.success.main
                      : theme.palette.secondary.main,
                }}
              >
                <b>{`${t(`mail_classification_statuses.${incomingMail.classificationStatus?.name}`)}`}</b>
              </Typography>
              {incomingMail.classificationStatus?.id === MAIL_CLASSIFICATION_STATUS.Classified.id && (
                <>
                  <Typography variant="body2">{incomingMail.document?.legalEntity?.code}</Typography>
                  <Typography variant="body2">{incomingMail.document?.type?.name}</Typography>
                  <Typography
                    variant="body2"
                    color={theme.palette.secondary.main}
                  >
                    {incomingMail.document?.type?.dateInsteadPeriod && incomingMail.document?.documentDate ? (
                      <>{intlFormatDateShort(incomingMail.document?.documentDate)}</>
                    ) : (
                      <>{getItemPeriodValue(incomingMail.document?.period ?? null)}</>
                    )}
                  </Typography>
                </>
              )}
            </StatusContainer>
          </BlockStatuses>

          <BlockEdit flex={0.1}>
            <IncomingMailActions
              incomingMail={incomingMail}
              handleNotifyParent={handleNotifyParent}
              canUpload={canUpload}
              canClassify={canClassify}
            />
          </BlockEdit>
        </StyledCard>
      </CardWrapper>

      {viewFile && incomingMail.document?.files[0] && (
        <DocumentViewerContainer open={viewFile}>
          <DialogTitle
            title=""
            handleClose={() => {
              setViewFile(false);
            }}
          />
          <Grid style={{ width: "90vw" }}>
            <DocumentViewer
              fileDto={incomingMail.document.files[0]}
              withFooter
              {...(!incomingMail.document.companyAccount && {
                isGenericDocument: true,
              })}
              bypasAllowDownload
            />
          </Grid>
        </DocumentViewerContainer>
      )}
    </>
  );
};

export default IncomingMailCard;
